import { Align, alignOptions } from "../../components/typography"
import { Color, TypographyColor } from "../../components/typography"
import ImageEditor, { AspectRatio } from "../../Image"
import { Link, Repeater, types } from "react-bricks/frontend"
import Typography, { TypographyElement } from "../../Typography"

import { BackgroundColor } from "../../components/layout"
import React from "react"
import { TextLinkBrickProps } from "../../text/TextLink"
import { TextLinkSize } from "../../components/text"
import { TypographyAlign } from "../../components/typography"
import classNames from "classnames"

export interface ImageTextSliderItemProps {
    text: TypographyElement[]
    subText: TypographyElement[]
    index?: number
    bgColor?: BackgroundColor
    align?: TypographyAlign
    itemLink?: string
}

const ImageTextSliderItem: types.Brick<ImageTextSliderItemProps> = ({
    index,
    bgColor,
    align = TypographyAlign["Left + mobile center"],
    itemLink,
}) => {
    const isExternalLink = !!itemLink && !/^\/(?!\/)/i.test(itemLink)
    const textColor =
        bgColor === BackgroundColor.Green || bgColor === BackgroundColor.DarkBlumine
            ? TypographyColor.White
            : TypographyColor.Darkgrey

    const content = (
        <>
            <div className="aspect-h-4 aspect-w-5">
                <div>
                    <ImageEditor
                        aspectRatio={AspectRatio["5/4"]}
                        maxWidth={717}
                        className="overflow-hidden rounded-base"
                    />
                </div>
            </div>
            <Align align={align} className="space-y-3.5 lg:space-y-5">
                <Color color={textColor}>
                    <Typography
                        block="image-text-slider"
                        allowList={itemLink ? ["sup", "bold"] : ["sup", "link", "bold"]}
                        linkArrow={!!itemLink}
                    />
                    {itemLink && (
                        <span className="whitespace-nowrap">
                            <span className="ml-2 inline-block w-3.5 shrink-0 align-middle leading-none sm:w-4 lg:w-5.5 lg:-mt-[5px]">
                                <i
                                    className={classNames("lavita-icon inline-block leading-none", {
                                        "text-sm lg:text-base": isExternalLink,
                                        "text-base lg:text-md": !isExternalLink,
                                    })}
                                >
                                    {isExternalLink ? "" : ""}
                                </i>
                            </span>
                        </span>
                    )}
                </Color>
                {!itemLink ? (
                    <>
                        <Color color={textColor}>
                            <Typography
                                block="copytext-extra-small"
                                allowList={["small", "sup", "link"]}
                                propName="subText"
                            />
                        </Color>
                        <Repeater propName="repeater" itemProps={{ size: TextLinkSize.Small } as TextLinkBrickProps} />
                    </>
                ) : null}
            </Align>
        </>
    )

    return (
        <li key={index} className="flex flex-col space-y-7 sm:space-y-5 md:space-y-7">
            {itemLink ? (
                <Link
                    href={itemLink}
                    target={isExternalLink ? "_blank" : undefined}
                    rel={isExternalLink ? "noopener noreferrer" : undefined}
                    className="block cursor-pointer! space-y-7 sm:space-y-5 md:space-y-7 [&_*]:cursor-pointer!"
                >
                    {content}
                </Link>
            ) : (
                content
            )}
        </li>
    )
}

ImageTextSliderItem.schema = {
    name: "ImageTextSliderItem",
    label: "Image Text Slider Item",
    hideFromAddMenu: true,
    repeaterItems: [
        {
            name: "repeater",
            items: [
                {
                    type: "TextLink",
                    label: "Text Link",
                    max: 1,
                },
            ],
        },
    ],
    sideEditProps: [
        {
            name: "itemLink",
            label: "Item Link",
            type: types.SideEditPropType.Text,
        },
        {
            name: "align",
            label: "Align",
            type: types.SideEditPropType.Select,
            selectOptions: {
                options: alignOptions.sort((a, b) => (a.value === TypographyAlign["Left + mobile center"] ? -1 : 1)),
                display: types.OptionsDisplay.Select,
            },
        },
    ],
    getDefaultProps: (): Omit<ImageTextSliderItemProps, "activeIndex"> => ({
        align: TypographyAlign["Left + mobile center"],
        text: [
            {
                type: "paragraph",
                children: [
                    {
                        bold: true,
                        text: "Lorem ipsum dolor sit amet, consetetur sadipscing elitr, sed diam nonumy eirmod tempor invidunt ut labore et dolore magna aliquyam erat, sed diam voluptua.",
                    },
                ],
            },
        ],
        subText: [
            {
                type: "paragraph",
                children: [
                    {
                        text: "Lorem ipsum, dolor sit amet",
                    },
                ],
            },
        ],
    }),
}

export default ImageTextSliderItem
